import { useState, useCallback, useMemo } from 'react';
import { ConfirmationModal } from '../components/common';
import PlayerInfoModal from '../components/players/PlayerInfoModal';
import MatchPreferenceSelector from '../components/players/MatchPreferenceSelector';
import MensDoublesEligibilityModal from '../components/players/MensDoublesEligibilityModal';
import { getEffectiveGamesThisSession } from '../utils/playerUtils';

const getPlayerDisplayName = (player) =>
  player?.name || `${player?.first_name || ''} ${player?.last_name || ''}`.trim() || 'Player';

/**
 * Shared competitive host action modals + menu context for Courts tab and PlayerManager.
 */
const useCompetitivePlayerHostActions = ({
  sessionId,
  seasonId,
  currentClubId,
  sessionPlayerStatus = {},
  sessionPlayerExitReasons = {},
  sessionPlayerPenalties = {},
  sessionPlayerScheduledGames = {},
  partnershipLookup = {},
  playersById = {},
  players = [],
  playerWaitMetrics = {},
  playersOnActiveCourts = null,
  playersInUpcomingCourts = null,
  matchPreferencesEnabled = true,
  hideMatchPreferenceMenu = false,
  hideLateJoinPenaltyMenu = false,
  hideMensDoublesEligibilityMenu = false,
  togglePlayerStatus,
  onGuestUpdated,
  onPenaltyUpdated,
  onMatchPreferenceSaved,
  onMatchPreferenceReload,
  onMensEligibilitySaved,
  offlineQueue = null,
  session = null,
  getSnapshot = null,
}) => {
  const [showPlayerInfoModal, setShowPlayerInfoModal] = useState(false);
  const [selectedPlayerForInfo, setSelectedPlayerForInfo] = useState(null);
  const [playerInfoModalInitialView, setPlayerInfoModalInitialView] = useState('info');
  const [playerInfoModalEditFocus, setPlayerInfoModalEditFocus] = useState('auto');
  const [preferenceModalPlayer, setPreferenceModalPlayer] = useState(null);
  const [eligibilityModalPlayer, setEligibilityModalPlayer] = useState(null);
  const [removeModalPlayer, setRemoveModalPlayer] = useState(null);
  const [removeLoading, setRemoveLoading] = useState(false);

  const sessionPlayingAvgEffectiveGames = useMemo(() => {
    if (!sessionId) return null;
    const activePlayers = players.filter((p) => sessionPlayerStatus[p.id] === 'active');
    if (activePlayers.length === 0) return null;
    const sum = activePlayers.reduce((acc, p) => acc + getEffectiveGamesThisSession(p), 0);
    const average = sum / activePlayers.length;
    if (!Number.isFinite(average)) return null;
    return { average, count: activePlayers.length };
  }, [sessionId, players, sessionPlayerStatus]);

  const openPlayerInfoModal = useCallback((player, arg = 'info') => {
    let initialViewMode = 'info';
    let initialEditFocus = 'auto';
    if (typeof arg === 'string') {
      initialViewMode = arg;
    } else if (arg && typeof arg === 'object') {
      initialViewMode = arg.initialViewMode ?? 'info';
      initialEditFocus = arg.initialEditFocus ?? 'auto';
    }
    setPlayerInfoModalInitialView(initialViewMode);
    setPlayerInfoModalEditFocus(initialEditFocus);
    setSelectedPlayerForInfo(player);
    setShowPlayerInfoModal(true);
  }, []);

  const closePlayerInfoModal = useCallback(() => {
    setShowPlayerInfoModal(false);
    setSelectedPlayerForInfo(null);
    setPlayerInfoModalInitialView('info');
    setPlayerInfoModalEditFocus('auto');
  }, []);

  const handleConfirmRemove = useCallback(async () => {
    if (!removeModalPlayer || !togglePlayerStatus) return;
    setRemoveLoading(true);
    try {
      const success = await togglePlayerStatus(removeModalPlayer.id, 'remove');
      if (success !== false) {
        setRemoveModalPlayer(null);
      }
    } finally {
      setRemoveLoading(false);
    }
  }, [removeModalPlayer, togglePlayerStatus]);

  const hostActionContext = useMemo(
    () => ({
      sessionId,
      sessionPlayerStatus,
      sessionPlayerExitReasons,
      sessionPlayerScheduledGames,
      partnershipLookup,
      playersById,
      playersOnActiveCourts,
      playersInUpcomingCourts,
      matchPreferencesEnabled,
      hideMatchPreferenceMenu,
      hideLateJoinPenaltyMenu,
      hideMensDoublesEligibilityMenu,
      togglePlayerStatus,
      openPlayerInfoModal,
      setPreferenceModalPlayer,
      setEligibilityModalPlayer,
      setRemoveModalPlayer,
    }),
    [
      sessionId,
      sessionPlayerStatus,
      sessionPlayerExitReasons,
      sessionPlayerScheduledGames,
      partnershipLookup,
      playersById,
      playersOnActiveCourts,
      playersInUpcomingCourts,
      matchPreferencesEnabled,
      hideMatchPreferenceMenu,
      hideLateJoinPenaltyMenu,
      hideMensDoublesEligibilityMenu,
      togglePlayerStatus,
      openPlayerInfoModal,
    ]
  );

  const renderHostActionModals = () => (
    <>
      {showPlayerInfoModal && selectedPlayerForInfo && (
        <PlayerInfoModal
          isOpen={showPlayerInfoModal}
          onClose={closePlayerInfoModal}
          player={selectedPlayerForInfo}
          clubId={currentClubId}
          seasonId={seasonId}
          sessionId={sessionId}
          onGuestUpdated={onGuestUpdated}
          onPenaltyUpdated={onPenaltyUpdated}
          sessionPlayerStatus={sessionPlayerStatus}
          sessionPlayerPenalties={sessionPlayerPenalties}
          initialViewMode={playerInfoModalInitialView}
          initialEditFocus={playerInfoModalEditFocus}
          allowLateJoinPenalty={!hideLateJoinPenaltyMenu}
          sessionPlayingAvgEffectiveGames={sessionPlayingAvgEffectiveGames}
        />
      )}

      {preferenceModalPlayer?.is_guest && sessionId && (
        <MatchPreferenceSelector
          variant="modal"
          open
          onClose={() => setPreferenceModalPlayer(null)}
          playerId={preferenceModalPlayer.id}
          sessionId={sessionId}
          currentPreference={
            playerWaitMetrics[preferenceModalPlayer.id]?.matchPreference || 'no_preference'
          }
          currentStrength={playerWaitMetrics[preferenceModalPlayer.id]?.preferenceStrength || 'soft'}
          playerName={getPlayerDisplayName(preferenceModalPlayer)}
          offlineQueue={offlineQueue}
          session={session}
          getSnapshot={getSnapshot}
          onPreferenceChange={(playerId, newPref, newStrength, rowUpdatedAt) => {
            if (onMatchPreferenceSaved) {
              onMatchPreferenceSaved(playerId, newPref, newStrength, rowUpdatedAt);
            }
            if (onMatchPreferenceReload) onMatchPreferenceReload();
            setPreferenceModalPlayer(null);
          }}
        />
      )}

      {eligibilityModalPlayer && sessionId && (
        <MensDoublesEligibilityModal
          open
          onClose={() => setEligibilityModalPlayer(null)}
          player={eligibilityModalPlayer}
          sessionId={sessionId}
          currentEligible={playerWaitMetrics[eligibilityModalPlayer.id]?.mensDoublesEligible || false}
          currentShadowElo={
            playerWaitMetrics[eligibilityModalPlayer.id]?.mensDoublesShadowElo ?? null
          }
          playersById={playersById}
          onSaved={(playerId, eligible, shadowElo) => {
            if (onMensEligibilitySaved) onMensEligibilitySaved(playerId, eligible, shadowElo);
          }}
        />
      )}

      <ConfirmationModal
        isOpen={!!removeModalPlayer}
        onClose={() => setRemoveModalPlayer(null)}
        onConfirm={handleConfirmRemove}
        title="Remove from session"
        message={
          removeModalPlayer
            ? `Remove ${getPlayerDisplayName(removeModalPlayer)} from this session? They will return to the available list.`
            : ''
        }
        confirmText="Remove"
        isDangerous
        loading={removeLoading}
      />
    </>
  );

  return {
    hostActionContext,
    openPlayerInfoModal,
    closePlayerInfoModal,
    renderHostActionModals,
  };
};

export default useCompetitivePlayerHostActions;
